import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Grid,
  Autocomplete,
  Chip,
  Collapse,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import IosShareIcon from "@mui/icons-material/IosShare";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// =====================================================================
// Constants (unchanged)
// =====================================================================
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// The client list is read back out of trip_booking, so it grows with every new
// customer an established store has ever driven for. MUI paints every match
// into the DOM - it does not virtualise - so cap what one open can render.
// Typing narrows the list long before the cap is reached.
const limitedOptions = createFilterOptions({ limit: 100 });

// A client is as often known by the number on the booking as by the name, so
// both are what the typed text is matched against.
const clientFilterOptions = createFilterOptions({
  limit: 100,
  stringify: (option) =>
    `${option.name || ""} ${(option.mobiles || []).join(" ")}`,
});

// Rows shown at once. Every row mounts two date pickers and five inputs, so
// this is a rendering budget as much as a preference - hence no "All".
const PAGE_SIZES = [25, 50, 100, 200];

const calculateKmRun = (startKm, endKm) => {
  const start = parseFloat(startKm) || 0;
  const end = parseFloat(endKm) || 0;
  return end >= start ? end - start : 0;
};

// Static, and shared by every row - kept out of the render so they are not
// fresh objects that would defeat TripRow's memoisation.
const cellSx = { py: 0.4, px: 0.75, fontSize: "0.85rem" };

const kmFieldSx = {
  "& .MuiInputBase-input": {
    fontSize: "0.75rem",
    padding: "3px 6px" /* was 4px 8px */,
    textAlign: "right",
  },
  "& .MuiOutlinedInput-root": { height: 26 },
};

// =====================================================================
// Calendar helpers (all unchanged)
// =====================================================================
function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      outside: true,
    });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  return cells;
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function to12h(time24) {
  if (!time24) return { h12: "12", mm: "00", period: "AM" };
  const [rawH, rawM = "00"] = time24.split(":");
  let h = parseInt(rawH, 10);
  if (isNaN(h)) return { h12: "12", mm: "00", period: "AM" };
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { h12: String(h), mm: rawM.padStart(2, "0"), period };
}

function to24h(h12, mm, period) {
  let h = parseInt(h12, 10);
  if (isNaN(h)) h = 12;
  if (period === "AM") {
    if (h === 12) h = 0;
  } else {
    if (h !== 12) h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// =====================================================================
// TimeSelector — tightened vertical spacing only
// =====================================================================
const TimeSelector = ({ time24, onChange, label }) => {
  const isUnset = !time24;
  const { h12, mm, period } = to12h(time24 || "");
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0"),
  );
  const safeH = isUnset ? "12" : h12;
  const safeM = isUnset ? "00" : mm;
  const safeP = isUnset ? "AM" : period;

  const selectSx = {
    border: "1px solid #ccc",
    borderRadius: "3px",
    fontSize: "0.72rem",
    padding: "1px 3px",
    cursor: "pointer",
    outline: "none",
    background: "#f9f9f9",
    fontWeight: 600,
    height: 22,
  };
  const placeholderSx = { ...selectSx, color: "#aaa" };

  return (
    /* mb reduced from 1 → 0.4 */
    <Box sx={{ textAlign: "center", mb: 0.4 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.3,
          fontWeight: 600,
          color: "#555",
          fontSize: "0.65rem",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "3px",
        }}
      >
        <Box
          component="select"
          value={isUnset ? "" : h12}
          onChange={(e) => onChange(to24h(e.target.value, safeM, safeP))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              HH
            </option>
          )}
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.8rem",
            color: isUnset ? "#aaa" : "inherit",
            lineHeight: 1,
          }}
        >
          :
        </Typography>
        <Box
          component="select"
          value={isUnset ? "" : mm}
          onChange={(e) => onChange(to24h(safeH, e.target.value, safeP))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              MM
            </option>
          )}
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Box>
        <Box
          component="select"
          value={safeP}
          onChange={(e) => onChange(to24h(safeH, safeM, e.target.value))}
          sx={isUnset ? placeholderSx : selectSx}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
// SingleDateTimePicker — compact trigger button + tighter dropdown
// =====================================================================
const SingleDateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  label = "Date & Time",
  minDate = null,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const today = new Date();
  const [calYear, setCalYear] = useState(
    () => fromYMD(date)?.getFullYear() ?? today.getFullYear(),
  );
  const [calMonth, setCalMonth] = useState(
    () => fromYMD(date)?.getMonth() ?? today.getMonth(),
  );

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 340;
    const dropdownWidth = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= dropdownHeight
        ? rect.bottom + window.scrollY + 4
        : rect.top + window.scrollY - dropdownHeight - 4;
    const spaceRight = window.innerWidth - rect.left;
    const left =
      spaceRight >= dropdownWidth
        ? rect.left + window.scrollX
        : rect.right + window.scrollX - dropdownWidth;
    setDropdownPos({ top, left });
  };

  const handleOpen = () => {
    updatePosition();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const selectedDateObj = fromYMD(date);

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };

  const handleDayClick = (d) => onDateChange(toYMD(d));
  // Only the open dropdown needs a calendar. Building this for every closed
  // picker meant 42 Date objects per picker on every render of the table.
  const cells = open ? buildCalendarDays(calYear, calMonth) : [];

  // Day-month only. The grid already shows one selected day at a time and the
  // month sits in the header, so the year is noise in a narrow column.
  const fmtDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-");
    if (!y || !m || !d) return ymd;
    return `${d}-${m}`;
  };
  const fmtTime = (t) => {
    if (!t) return "Time?";
    const { h12, mm, period } = to12h(t);
    return `${h12}:${mm} ${period}`;
  };

  const displayText = date ? `${fmtDate(date)}  ${fmtTime(time)}` : label;

  const dropdownEl = open
    ? ReactDOM.createPortal(
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            position: "absolute",
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 99999,
            p: 1.5 /* reduced from p:2 */,
            borderRadius: 2,
            width: 240 /* reduced from 270 */,
          }}
        >
          {/* Time picker section — tighter border box */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 0.75,
              mb: 1,
            }}
          >
            <TimeSelector label={label} time24={time} onChange={onTimeChange} />
          </Box>

          {/* Calendar */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <IconButton size="small" onClick={prevMonth} sx={{ p: 0.3 }}>
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: "0.78rem" }}
              >
                {MONTH_NAMES[calMonth]} {calYear}
              </Typography>
              <IconButton size="small" onClick={nextMonth} sx={{ p: 0.3 }}>
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Day headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                mb: 0.3,
              }}
            >
              {DAY_HEADERS.map((d) => (
                <Typography
                  key={d}
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#888",
                    fontSize: "0.62rem",
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>

            {/* Day cells — height reduced from 30 → 24 */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: 0,
              }}
            >
              {cells.map((cell, idx) => {
                const { date: cellDate, outside } = cell;
                const isSelected = isSameDay(cellDate, selectedDateObj);
                const isDisabled =
                  outside || (minDate && toYMD(cellDate) < minDate);
                let bg = "transparent";
                let color = isDisabled ? "#ccc" : "#222";
                let fontWeight = 400;
                if (isSelected) {
                  bg = "#1976d2";
                  color = "#fff";
                  fontWeight = 700;
                }
                return (
                  <Box
                    key={idx}
                    onClick={() => !isDisabled && handleDayClick(cellDate)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 24 /* was 30 */,
                      bgcolor: bg,
                      borderRadius: "50%",
                      cursor: isDisabled ? "default" : "pointer",
                      "&:hover": !isDisabled
                        ? { bgcolor: isSelected ? "#1565c0" : "#bbdefb" }
                        : {},
                      transition: "background 0.1s",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight,
                        color,
                        lineHeight: 1,
                        userSelect: "none",
                      }}
                    >
                      {cellDate.getDate()}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Footer buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.72rem", py: 0.3, minWidth: 0 }}
              onClick={() => {
                onDateChange("");
                onTimeChange("");
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "0.72rem", py: 0.3 }}
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </Box>
        </Paper>,
        document.body,
      )
    : null;

  return (
    <Box ref={triggerRef} sx={{ display: "block", width: "100%" }}>
      {/* Trigger chip — height reduced from ~32px → 24px */}
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.4,
          border: open ? "1.5px solid #1976d2" : "1px solid rgba(0,0,0,0.2)",
          borderRadius: "4px",
          px: 0.75,
          py: "3px" /* was 5px */,
          cursor: "pointer",
          bgcolor: "#fff",
          // Sized for "05-08  10:30 AM" - no year to fit any more.
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          "&:hover": { borderColor: "#333" },
        }}
      >
        <CalendarTodayIcon
          sx={{ fontSize: 11, color: open ? "#1976d2" : "#888", flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8rem" /* was 0.72rem */,
            color: date ? "#222" : "#999",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
          }}
        >
          {displayText}
        </Typography>
      </Box>
      {dropdownEl}
    </Box>
  );
};

// =====================================================================
// Helpers (unchanged)
// =====================================================================
const getDaysInMonth = (monthYearStr) => {
  const [monthStr, yearStr] = monthYearStr.split(" ");
  const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
  const year = parseInt(yearStr);
  return new Date(year, monthIndex + 1, 0).getDate();
};

const formatTime24 = (time) => {
  if (!time) return "";
  return time.substring(0, 5);
};

// "2026-09-01" -> "01-09-2026", for the filtered header and file names.
const formatYMDToDMY = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}-${m}-${y}` : ymd;
};

// =====================================================================
// TripRow
//
// Memoised on purpose. Every keystroke in a KM / Bata / Hire field
// updates editingRows on the parent, and without this every row on the
// page - each carrying two date pickers and five inputs - re-rendered
// on every character. Only the row being edited re-renders now, which
// is what makes the larger page sizes usable.
// =====================================================================
const TripRow = React.memo(function TripRow({
  row,
  edited,
  isSaving,
  isExpanded,
  handleFieldChange,
  handleSaveRow,
  toggleTripExpand,
}) {
  const bookingId = row.booking.booking_id;
  const hasChanges = edited?.hasChanges;
  const tripDetails = row.booking.trip_details || "";

  // Determine if the text is long enough to need a toggle
  const showToggle = tripDetails.length > 50;

  // Same shape as the old component-level helper, so the cells below
  // read exactly as they did before the move.
  const getEditedValue = (id, field, defaultValue) =>
    edited?.[field] ?? defaultValue;

  return (
      <TableRow
        hover
        sx={{
          gap: 2,
          backgroundColor: hasChanges
            ? "rgba(56,115,243,0.05)"
            : "inherit",
        }}
      >
        {/* Vehicle */}
        <TableCell sx={cellSx}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
            {row.vehicle_code}
          </Typography>
        </TableCell>
        {/* Driver */}
        <TableCell sx={cellSx}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
            {row.booking.driver_code || "-"}
          </Typography>
        </TableCell>
        {/* Start Date & Time */}
        <TableCell sx={cellSx}>
          <SingleDateTimePicker
            label="Start"
            date={getEditedValue(
              bookingId,
              "pickup_date",
              row.booking.trip_info?.pickup_date || "",
            )}
            time={getEditedValue(
              bookingId,
              "pickup_time",
              formatTime24(row.booking.trip_info?.pickup_time),
            )}
            onDateChange={(val) =>
              handleFieldChange(bookingId, "pickup_date", val)
            }
            onTimeChange={(val) =>
              handleFieldChange(bookingId, "pickup_time", val)
            }
          />
        </TableCell>
        {/* End Date & Time */}
        <TableCell sx={cellSx}>
          <SingleDateTimePicker
            label="End"
            date={getEditedValue(
              bookingId,
              "drop_date",
              row.booking.trip_info?.drop_date || "",
            )}
            time={getEditedValue(
              bookingId,
              "drop_time",
              formatTime24(row.booking.trip_info?.drop_time),
            )}
            onDateChange={(val) =>
              handleFieldChange(bookingId, "drop_date", val)
            }
            onTimeChange={(val) =>
              handleFieldChange(bookingId, "drop_time", val)
            }
            minDate={getEditedValue(
              bookingId,
              "pickup_date",
              row.booking.trip_info?.pickup_date || null,
            )}
          />
        </TableCell>
        {/* Vehicle Type */}
        <TableCell sx={cellSx}>
          <Typography sx={{ fontSize: "0.85rem" }}>
            {row.booking.trip_info?.vehicle_type || "N/A"}
          </Typography>
        </TableCell>
        {/* Customer */}
        <TableCell sx={cellSx}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "primary.main",
              lineHeight: 1.2,
            }}
          >
            {row.booking.customer_name}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              lineHeight: 1.2,
            }}
          >
            {row.booking.customer_mobile}
          </Typography>
        </TableCell>
        {/* Trip Details */}
        {/* <TableCell sx={cellSx}>
          <Typography sx={{ fontSize: "0.85rem" }}>
            {row.booking.trip_details || "N/A"}
          </Typography>
        </TableCell> */}{" "}
        <TableCell sx={cellSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
            }}
          >
            {/* The text container with smooth max-height transition */}
            <Box
              sx={{
                maxHeight: isExpanded ? "500px" : "4em", // 3 lines collapsed, large value when expanded
                overflow: "hidden",
                transition: "max-height 0.4s ease",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {tripDetails || "N/A"}
              </Typography>
            </Box>
            {/* Toggle arrow (only if text is long) */}
            {showToggle && (
              <IconButton
                size="small"
                onClick={() => toggleTripExpand(bookingId)}
                sx={{ p: 0, flexShrink: 0, mt: 0.3 }}
              >
                {isExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            )}
          </Box>
        </TableCell>
        {/* Start KM */}
        <TableCell sx={cellSx}>
          <TextField
            type="text"
            inputMode="numeric"
            pattern="[0-9.]*"
            value={getEditedValue(
              bookingId,
              "start_km",
              row.booking.trip_info?.start_km || "",
            )}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.]/g, "");
              handleFieldChange(bookingId, "start_km", val);
            }}
            size="small"
            fullWidth
            sx={kmFieldSx}
          />
        </TableCell>
        {/* End KM */}
        <TableCell sx={cellSx}>
          <TextField
            type="text"
            inputMode="numeric"
            pattern="[0-9.]*"
            value={getEditedValue(
              bookingId,
              "end_km",
              row.booking.trip_info?.end_km || "",
            )}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.]/g, "");
              handleFieldChange(bookingId, "end_km", val);
            }}
            size="small"
            fullWidth
            sx={kmFieldSx}
          />
        </TableCell>
        {/* KM Run */}
        <TableCell sx={cellSx}>
          <TextField
            disabled
            type="text"
            inputMode="numeric"
            pattern="[0-9.]*"
            value={getEditedValue(
              bookingId,
              "km_run",
              calculateKmRun(
                getEditedValue(
                  bookingId,
                  "start_km",
                  row.booking.trip_info?.start_km,
                ),
                getEditedValue(
                  bookingId,
                  "end_km",
                  row.booking.trip_info?.end_km,
                ),
              ).toString(),
            )}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.]/g, "");
              handleFieldChange(bookingId, "km_run", val);
            }}
            size="small"
            fullWidth
            sx={kmFieldSx}
          />
        </TableCell>
        {/* Driver Bata */}
        <TableCell sx={cellSx}>
          <TextField
            type="text"
            inputMode="numeric"
            pattern="[0-9.]*"
            value={getEditedValue(
              bookingId,
              "driver_bata",
              row.booking.trip_info?.driver_bata || "",
            )}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9.]/g, "");
              handleFieldChange(bookingId, "driver_bata", val);
            }}
            size="small"
            fullWidth
            sx={kmFieldSx}
          />
        </TableCell>
        {/* Hire */}
        <TableCell sx={cellSx}>
          <TextField
            type="text"
            inputMode="numeric"
            pattern="[0-9.]*"
            value={getEditedValue(
              bookingId,
              "hire",
              row.booking.trip_info?.hire ?? "",
            )}
            onChange={(e) => {
              // Whole rupees only - the column is INT
              const val = e.target.value.replace(/[^0-9]/g, "");
              handleFieldChange(bookingId, "hire", val);
            }}
            size="small"
            fullWidth
            sx={kmFieldSx}
          />
        </TableCell>
        {/* Action */}
        <TableCell sx={cellSx}>
          {hasChanges && (
            <IconButton
              onClick={() => handleSaveRow(bookingId)}
              disabled={isSaving}
              size="small"
              color="primary"
              sx={{
                p: 0.4 /* tighter padding */,
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": { bgcolor: "grey.300" },
              }}
            >
              {isSaving ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                <SaveIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
  );
});

// =====================================================================
// Main Component
// =====================================================================
export default function TripEntryDashboard({
  selectedMonth,
  selectedDate,
  setSelectedDate,
  activeFilter,
  vehiclesData,
  onRefresh,
  onNavigateToBooking,
  accountIds,
  setSelectedMonth,
}) {
  console.log(vehiclesData);
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  const loggedUser = JSON.parse(localStorage.getItem("CapacitorStorage.user"));
  const user_id = loggedUser?.id;

  const [editingRows, setEditingRows] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [expandedRows, setExpandedRows] = useState({});
  // The three callbacks below are handed to every TripRow, so they have to
  // keep their identity across renders or the memoisation there buys nothing.
  const toggleTripExpand = useCallback((bookingId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  }, []);
  // ─────────────────────────────────────────────────────────────────────
  // Filter search
  //
  // The tab normally shows one day of the selected month, picked out of the
  // month payload the parent already holds. A filter search swaps that
  // source for a server query over a date range - same vehicle -> bookings
  // shape, so the rows below stay exactly as editable as they were.
  // ─────────────────────────────────────────────────────────────────────
  const [filterOpen, setFilterOpen] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // searchData null = the ordinary day view is in charge.
  const [searchData, setSearchData] = useState(null);
  const [searching, setSearching] = useState(false);
  const [filterError, setFilterError] = useState("");
  // The filters that produced searchData, so the header keeps describing
  // what is on screen even after the inputs are changed but not re-run.
  const [appliedFilters, setAppliedFilters] = useState(null);

  const monthBounds = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);
    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    return {
      from: `${year}-${pad(monthIndex + 1)}-01`,
      to: `${year}-${pad(monthIndex + 1)}-${pad(lastDay)}`,
    };
  }, [selectedMonth]);

  // Seed the range from whichever month the dashboard is on, so opening the
  // panel already describes what is on screen. A live search owns the
  // inputs from then on.
  useEffect(() => {
    if (searchData) return;
    setFromDate(monthBounds.from);
    setToDate(monthBounds.to);
  }, [monthBounds, searchData]);

  // Dropdown contents are only worth three requests once the panel is
  // actually opened.
  const optionsLoaded = useRef(false);
  useEffect(() => {
    if (!filterOpen || !store_id || optionsLoaded.current) return;
    optionsLoaded.current = true;
    const base = process.env.REACT_APP_API_BASE_URL;
    setLoadingOptions(true);
    Promise.all([
      axios.get(`${base}/api/travel/vehicles/store/${store_id}`),
      axios.get(`${base}/api/travel/drivers/store/${store_id}`),
      axios.get(`${base}/api/accounts/trip-report/filters/${store_id}`),
    ])
      .then(([vehicleRes, driverRes, filterRes]) => {
        if (vehicleRes.data?.success)
          setVehicleOptions(vehicleRes.data.data || []);
        if (driverRes.data?.success)
          setDriverOptions(driverRes.data.data || []);
        if (filterRes.data?.success) {
          // clientOptions carries the mobile numbers; clients is the older
          // name-only list, kept as a fallback so this panel still fills if
          // it is talking to a server that predates the richer field.
          const withMobiles = filterRes.data.data?.clientOptions;
          setClientOptions(
            Array.isArray(withMobiles) && withMobiles.length > 0
              ? withMobiles
              : (filterRes.data.data?.clients || []).map((name) => ({
                  name,
                  mobiles: [],
                })),
          );
        }
      })
      .catch((err) => {
        console.error("Error loading trip filter options:", err);
        optionsLoaded.current = false; // let the next open retry
        setFilterError("Could not load the filter options");
      })
      .finally(() => setLoadingOptions(false));
  }, [filterOpen, store_id]);

  // Every filter is optional - omitting one simply widens the search.
  const fetchFiltered = async (filters) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/dashboard/search/${store_id}`,
      {
        from_date: filters.fromDate,
        to_date: filters.toDate,
        vehicle_id: filters.vehicle?.vehicle_id ?? null,
        driver_id: filters.driver?.id ?? null,
        client: filters.client?.name || null,
      },
    );
    if (!response.data.success)
      throw new Error(response.data.statusMessage || "Failed to load trips");
    return response.data.data || [];
  };

  const handleFilter = async () => {
    setFilterError("");
    if (!fromDate || !toDate) {
      setFilterError("Please select a date range");
      return;
    }
    if (fromDate > toDate) {
      setFilterError("From Date cannot be after To Date");
      return;
    }
    const filters = {
      vehicle: selectedVehicle,
      driver: selectedDriver,
      client: selectedClient,
      fromDate,
      toDate,
    };
    setSearching(true);
    try {
      setSearchData(await fetchFiltered(filters));
      setAppliedFilters(filters);
    } catch (error) {
      console.error("Trip filter error:", error);
      setFilterError(
        error?.response?.data?.statusMessage ||
          error?.message ||
          "Error loading filtered trips",
      );
    } finally {
      setSearching(false);
    }
  };

  // Back to the day view. The inputs reseed from the selected month.
  const clearFilter = () => {
    setSearchData(null);
    setAppliedFilters(null);
    setFilterError("");
  };

  const handleFilterReset = () => {
    setSelectedVehicle(null);
    setSelectedDriver(null);
    setSelectedClient(null);
    clearFilter();
  };

  // Picking another month in the header is a "take me somewhere else"
  // gesture, so a filtered range must not survive it - the table would sit
  // there unchanged and look broken.
  const lastMonth = useRef(selectedMonth);
  useEffect(() => {
    if (lastMonth.current === selectedMonth) return;
    lastMonth.current = selectedMonth;
    clearFilter();
  }, [selectedMonth]);

  // A row save has to reload from wherever the rows came from - refetching
  // the month while a filter is on screen would leave the saved value
  // looking lost.
  const refreshRows = async () => {
    if (!appliedFilters) return onRefresh?.();
    try {
      setSearchData(await fetchFiltered(appliedFilters));
    } catch (error) {
      console.error("Trip filter refresh error:", error);
    }
  };

  const filterChips = useMemo(() => {
    if (!appliedFilters) return [];
    const { vehicle, driver, client } = appliedFilters;
    const parts = [];
    if (vehicle)
      parts.push(
        `Vehicle ${vehicle.vehicle_code || vehicle.vehicle_name || ""}`,
      );
    if (driver) parts.push(`Driver ${driver.driver_code || driver.name || ""}`);
    if (client) parts.push(`Client ${client.name}`);
    return parts.length > 0 ? parts : ["All vehicles & drivers"];
  }, [appliedFilters]);

  const extendedDays = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);
    const days = [];

    const prevMonthLastDay = new Date(year, monthIndex, 0).getDate();
    for (let i = 4; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevMonth = monthIndex === 0 ? 11 : monthIndex - 1;
      const prevYear = monthIndex === 0 ? year - 1 : year;
      const monthShort = new Date(prevYear, prevMonth, 1).toLocaleString(
        "default",
        { month: "short" },
      );
      days.push({
        day,
        label: `${monthShort} ${prevYear}`,
        isCurrentMonth: false,
        isPrev: true,
      });
    }

    const numDays = new Date(year, monthIndex + 1, 0).getDate();
    const monthShort = new Date(year, monthIndex, 1).toLocaleString("default", {
      month: "short",
    });
    for (let d = 1; d <= numDays; d++) {
      days.push({
        day: d,
        label: `${monthShort} ${year}`,
        isCurrentMonth: true,
        isPrev: false,
      });
    }

    const nextMonth = monthIndex === 11 ? 0 : monthIndex + 1;
    const nextYear = monthIndex === 11 ? year + 1 : year;
    const nextMonthShort = new Date(nextYear, nextMonth, 1).toLocaleString(
      "default",
      { month: "short" },
    );
    for (let d = 1; d <= 5; d++) {
      days.push({
        day: d,
        label: `${nextMonthShort} ${nextYear}`,
        isCurrentMonth: false,
        isPrev: false,
      });
    }

    return days;
  }, [selectedMonth]);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hr = parseInt(hours);
    const ampm = hr >= 12 ? "PM" : "AM";
    const displayHr = hr % 12 || 12;
    return `${displayHr}:${minutes} ${ampm}`;
  };

  // Both sources hand back the same vehicle -> bookings payload, so a row
  // is built the same way either way.
  const toRow = (vehicle, booking) => ({
    vehicle_id: vehicle.vehicle_id,
    vehicle_name: vehicle.vehicle_name,
    vehicle_number: vehicle.vehicle_number,
    vehicle_code: vehicle.vehicle_code,
    booking,
  });

  const processedBookings = useMemo(() => {
    // Filtered: every trip the search returned, in date order. The day strip
    // is out of the picture - each row carries its own start date.
    if (searchData) {
      const rows = [];
      searchData
        .filter((vehicle) => !vehicle.is_temp)
        .forEach((vehicle) =>
          vehicle.bookings.forEach((booking) =>
            rows.push(toRow(vehicle, booking)),
          ),
        );
      return rows.sort((a, b) => {
        const dateA = a.booking.trip_info?.pickup_date || "";
        const dateB = b.booking.trip_info?.pickup_date || "";
        if (dateA !== dateB) return dateA < dateB ? -1 : 1;
        const timeA = a.booking.trip_info?.pickup_time || "";
        const timeB = b.booking.trip_info?.pickup_time || "";
        return timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
      });
    }

    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);
    const targetDateStr = `${year}-${String(monthIndex + 1).padStart(
      2,
      "0",
    )}-${String(selectedDate).padStart(2, "0")}`;
    const result = [];

    vehiclesData
      .filter((vehicle) => !vehicle.is_temp)
      .forEach((vehicle) => {
        const bookingsForDate = vehicle.bookings.filter((booking) => {
          const pickupDate = booking.trip_info?.pickup_date;
          const dropDate = booking.trip_info?.drop_date;
          if (!pickupDate) return false;
          if (!dropDate || dropDate === null || dropDate === "")
            return pickupDate === targetDateStr;
          return targetDateStr >= pickupDate && targetDateStr <= dropDate;
        });
        bookingsForDate.forEach((booking) => {
          result.push(toRow(vehicle, booking));
        });
      });

    return result;
  }, [vehiclesData, selectedDate, selectedMonth, searchData]);

  const filteredBookings = useMemo(() => {
    // Hire drives Pending/Confirmed on this tab, and the two are exact
    // complements: hire is NOT NULL DEFAULT 0 in the DB, so "not entered yet"
    // and "zero" are the same state, and every trip falls in exactly one side.
    const hireOf = (row) => Number(row.booking.trip_info?.hire || 0);

    if (activeFilter === "PENDING")
      return processedBookings.filter((row) => hireOf(row) === 0);
    if (activeFilter === "CONFIRMED")
      return processedBookings.filter((row) => hireOf(row) > 0);
    return processedBookings;
  }, [processedBookings, activeFilter]);

  const handleFieldChange = useCallback((bookingId, field, value) => {
    setEditingRows((prev) => {
      const currentRow = prev[bookingId] || {};
      const newRow = { ...currentRow, [field]: value, hasChanges: true };
      if (field === "start_km" || field === "end_km") {
        const startKm = field === "start_km" ? value : currentRow.start_km;
        const endKm = field === "end_km" ? value : currentRow.end_km;
        if (startKm !== undefined && endKm !== undefined)
          newRow.km_run = calculateKmRun(startKm, endKm);
      }
      return { ...prev, [bookingId]: newRow };
    });
  }, []);

  const handleSaveRow = async (bookingId) => {
    const editedData = editingRows[bookingId];
    if (!editedData || !editedData.hasChanges) return;
    const bookingRow = filteredBookings.find(
      (row) => row.booking.booking_id === bookingId,
    );
    if (!bookingRow) return;
    const booking = bookingRow.booking;
    setSavingRows((prev) => ({ ...prev, [bookingId]: true }));
    try {
      const payload = {
        store_id,
        account_ids: accountIds,
        created_by: user_id,
        customer_id: booking.customer_id,
        booking_type: booking.type.includes("B") ? "B" : "E",
        customer_name: booking.customer_name,
        customer_mobile: booking.customer_mobile,
        trip_details: editedData.trip_details ?? booking.trip_details,
        pickup_date: editedData.pickup_date ?? booking.trip_info?.pickup_date,
        pickup_time: editedData.pickup_time ?? booking.trip_info?.pickup_time,
        pax: booking.trip_info?.pax,
        pickup_location: booking.trip_info?.pickup_location,
        pickup_address: booking.trip_info?.pickup_address,
        drop_location: booking.trip_info?.drop_location,
        drop_address: booking.trip_info?.drop_address,
        drop_date: editedData.drop_date ?? booking.trip_info?.drop_date,
        drop_time: editedData.drop_time ?? booking.trip_info?.drop_time,
        trip_mode: booking.trip_info?.mode,
        quote: parseFloat(booking.amount) || 0,
        advance: parseFloat(booking.advance) || 0,
        vehicle_id: booking.vehicle_id,
        driver_id: booking.driver_id,
        driver_phone: booking.driver_phone,
        driver_voucher_no: booking.driver_voucher_no,
        confirmation: booking.confirmation,
        payment_details: booking.trip_info?.payment_details,
        instructions: booking.instructions,
        vehicle_type:
          editedData.vehicle_type ?? booking.trip_info?.vehicle_type,
        isVehicleOther: false,
        isDriverOther: false,
        stage: booking.stage || "active",
        start_km: editedData.start_km ?? booking.trip_info?.start_km,
        end_km: editedData.end_km ?? booking.trip_info?.end_km,
        driver_bata: editedData.driver_bata ?? booking.trip_info?.driver_bata,
        hire: editedData.hire ?? booking.trip_info?.hire ?? 0,
      };
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/ledger/driverledger/${bookingId}`;
      const response = await axios.post(url, payload);
      if (response.data.success) {
        setSnackbar({
          open: true,
          message:
            response.data.statusMessage || "Trip details updated successfully!",
          severity: "success",
        });
        // Pull the saved row back from the server BEFORE dropping the local
        // draft. This component unmounts on a tab switch, taking editingRows
        // with it, so without a refetch the saved value appears to vanish
        // until a full page reload.
        await refreshRows();
        setEditingRows((prev) => {
          const next = { ...prev };
          delete next[bookingId];
          return next;
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || "Failed to update trip details",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.statusMessage ||
          error?.response?.data?.message ||
          "Error updating trip details",
        severity: "error",
      });
    } finally {
      setSavingRows((prev) => {
        const s = { ...prev };
        delete s[bookingId];
        return s;
      });
    }
  };

  // handleSaveRow closes over most of this component's state, so its
  // identity changes on every render. Rows only ever need the newest one, and
  // a prop that stops changing is what lets TripRow skip re-rendering.
  const saveRowRef = useRef(null);
  useEffect(() => {
    saveRowRef.current = handleSaveRow;
  });
  const onSaveRow = useCallback(
    (bookingId) => saveRowRef.current?.(bookingId),
    [],
  );

  const handleExcelExport = () => {
    if (filteredBookings.length === 0) {
      setSnackbar({
        open: true,
        message: "No trips to export",
        severity: "error",
      });
      return;
    }
    const excelData = filteredBookings.map((item, index) => ({
      "Sr. No": index + 1,
      Vehicle: item.vehicle_code,
      Driver: item.booking.driver_code || "N/A",
      "Start Time": formatTime(item.booking.trip_info?.pickup_time),
      "End Time": formatTime(item.booking.trip_info?.drop_time),
      "Vehicle Type": item.booking.trip_info?.vehicle_type || "N/A",
      "Customer Name": item.booking.customer_name,
      "Customer Mobile": item.booking.customer_mobile,
      "Trip Details": item.booking.trip_details || "N/A",
      "Start KM": item.booking.trip_info?.start_km || 0,
      "End KM": item.booking.trip_info?.end_km || 0,
      "KM Run": calculateKmRun(
        item.booking.trip_info?.start_km,
        item.booking.trip_info?.end_km,
      ),
      "Driver Bata": item.booking.trip_info?.driver_bata || "",
      Hire: item.booking.trip_info?.hire ?? 0,
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 30 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Trip Entry");
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const range = appliedFilters
      ? `${formatYMDToDMY(appliedFilters.fromDate)}_to_${formatYMDToDMY(
          appliedFilters.toDate,
        )}`
      : `${selectedDate}_${monthStr}_${yearStr}`;
    XLSX.writeFile(workbook, `Trip_Entry_${range}.xlsx`);
  };

  // A day holds a dozen trips; a filtered month holds hundreds, and each row
  // carries two date pickers and five inputs. Mounting them all at once locks
  // the tab up, so only a page of rows is ever on screen. The sizes stop at
  // 200 for that reason - past it the tab starts to crawl again.
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredBookings.length / rowsPerPage),
  );

  // A new row set starts over at the first page. Keyed on appliedFilters
  // rather than searchData so that saving a row - which refetches the same
  // filters - leaves the user on the page they were working through.
  useEffect(() => {
    setPage(0);
  }, [appliedFilters, selectedDate, selectedMonth, activeFilter]);

  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  const visibleBookings = useMemo(
    () => filteredBookings.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredBookings, page, rowsPerPage],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Sub-header: info row + day strip ── */}
      <Box
        sx={{
          px: 1.5,
          pt: 0.75 /* was p:2 */,
          pb: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* Info row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.6,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {appliedFilters
                ? `${formatYMDToDMY(
                    appliedFilters.fromDate,
                  )} to ${formatYMDToDMY(appliedFilters.toDate)}`
                : `${selectedDate} ${selectedMonth}`}{" "}
              • {filteredBookings.length} trips
            </Typography>

            {/* What the filtered table is showing, and the way back out */}
            {filterChips.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                color="primary"
                sx={{ height: 19, fontSize: "0.66rem" }}
              />
            ))}
            {appliedFilters && (
              <Chip
                label="Clear"
                size="small"
                variant="outlined"
                onClick={clearFilter}
                onDelete={clearFilter}
                deleteIcon={<CloseIcon sx={{ fontSize: 13 }} />}
                sx={{ height: 19, fontSize: "0.66rem" }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              flexShrink: 0,
            }}
          >
            {/* Pager - only earns its place once there are more rows than
                the smallest page size, so a normal day's header is as it
                was before. */}
            {filteredBookings.length > PAGE_SIZES[0] && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.3,
                  mr: 0.2,
                }}
              >
                <Box
                  component="select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "3px",
                    fontSize: "0.72rem",
                    padding: "1px 3px",
                    cursor: "pointer",
                    outline: "none",
                    background: "#f9f9f9",
                    fontWeight: 600,
                    height: 22,
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Box>

                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{ p: 0.3 }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {page * rowsPerPage + 1}-
                  {Math.min((page + 1) * rowsPerPage, filteredBookings.length)}{" "}
                  of {filteredBookings.length}
                </Typography>
                <IconButton
                  size="small"
                  disabled={page >= pageCount - 1}
                  onClick={() =>
                    setPage((prev) => Math.min(pageCount - 1, prev + 1))
                  }
                  sx={{ p: 0.3 }}
                >
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}

            {/* Filter panel toggle */}
            <Tooltip title="Filter trips" placement="left" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor:
                    filterOpen || appliedFilters ? "#3873f3" : "grey.200",
                  px: 1,
                  py: 0.4,
                  borderRadius: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <FilterAltIcon
                  sx={{
                    fontSize: 16,
                    color:
                      filterOpen || appliedFilters ? "#fff" : "text.secondary",
                  }}
                />
              </Box>
            </Tooltip>

            {/* Export button — compact */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#3873f3",
                px: 1,
                py: 0.4 /* was py:1 */,
                borderRadius: 1.5,
                cursor: "pointer",
              }}
              onClick={handleExcelExport}
            >
              <IosShareIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
          </Box>
        </Box>

        {/* ── Filter panel ──
            The same filter set as the Trip Report, but the result comes back
            in dashboard shape and lands in the table below, still editable. */}
        <Collapse in={filterOpen} unmountOnExit>
          <Box
            sx={{
              mb: 0.6,
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "grey.50",
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={4} sm={3} md={1}>
                <Autocomplete
                  size="small"
                  filterOptions={limitedOptions}
                  options={vehicleOptions}
                  value={selectedVehicle}
                  loading={loadingOptions}
                  onChange={(_, value) => setSelectedVehicle(value)}
                  getOptionLabel={(option) =>
                    option.vehicle_code || option.vehicle_name || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.vehicle_id === value?.vehicle_id
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Vehicle" placeholder="All" />
                  )}
                />
              </Grid>

              <Grid item xs={4} sm={3} md={1}>
                <Autocomplete
                  size="small"
                  filterOptions={limitedOptions}
                  options={driverOptions}
                  value={selectedDriver}
                  loading={loadingOptions}
                  onChange={(_, value) => setSelectedDriver(value)}
                  getOptionLabel={(option) =>
                    option.driver_code || option.code || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Driver" placeholder="All" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  size="small"
                  filterOptions={clientFilterOptions}
                  options={clientOptions}
                  value={selectedClient}
                  loading={loadingOptions}
                  onChange={(_, value) => setSelectedClient(value)}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value?.name
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.name}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: "0.78rem" }}>
                          {option.name}
                        </Typography>
                        {option.mobiles?.length > 0 && (
                          <Typography
                            noWrap
                            sx={{ fontSize: "0.68rem", color: "text.secondary" }}
                          >
                            {option.mobiles.join(", ")}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      placeholder="Name or mobile"
                    />
                  )}
                />
              </Grid>

              {/* The buttons sit in the sixth slot, where Place used to be,
                  so the panel stays one tidy row on a desktop width. */}
              <Grid item xs={12} sm={4} md={2} sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={searching}
                  onClick={handleFilter}
                  startIcon={
                    searching ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SearchIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{ fontSize: "0.72rem", whiteSpace: "nowrap", px: 1.2 }}
                >
                  {searching ? "Loading..." : "Filter"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleFilterReset}
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  sx={{ fontSize: "0.72rem", whiteSpace: "nowrap", px: 1.2 }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>

            {filterError && (
              <Alert
                severity="error"
                sx={{ mt: 1, py: 0, fontSize: "0.75rem" }}
              >
                {filterError}
              </Alert>
            )}
          </Box>
        </Collapse>

        {/* Day strip — chips reduced from 34px → 26px. Hidden while a
            filter search owns the table, where one day no longer
            describes what is on screen. */}
        {!searchData && (
          <Box sx={{ overflowX: "auto", pb: 0.4 }}>
            <Box sx={{ display: "flex", gap: 0.35, minWidth: "max-content" }}>
              {/* ── LEFT: previous‑month button ── */}
              {extendedDays.length > 0 && extendedDays[0].isPrev && (
                <Box
                  onClick={() => {
                    const prevItem = extendedDays[0];
                    setSelectedMonth(prevItem.label);
                    setSelectedDate(1);
                  }}
                  sx={{
                    minWidth: 26,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 0.75,
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: 400,
                    bgcolor: "grey.50",
                    color: "text.disabled",
                    border: "1px dashed #ccc",
                    px: 1,
                    "&:hover": {
                      bgcolor: "grey.200",
                      color: "text.primary",
                    },
                  }}
                >
                  {extendedDays[0].label.split(" ")[0]}
                </Box>
              )}

              {/* ── CURRENT MONTH DAYS ── */}
              {extendedDays
                .filter((item) => item.isCurrentMonth)
                .map((item, idx) => {
                  const isSelected = selectedDate === item.day;
                  return (
                    <Box
                      key={idx}
                      onClick={() => setSelectedDate(item.day)}
                      sx={{
                        minWidth: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 0.75,
                        cursor: "pointer",
                        fontSize: "0.72rem",
                        fontWeight: isSelected ? 600 : 400,
                        bgcolor: isSelected ? "primary.main" : "grey.100",
                        color: isSelected ? "white" : "text.primary",
                        border: "none",
                        "&:hover": {
                          bgcolor: isSelected ? "primary.dark" : "grey.200",
                          color: isSelected ? "white" : "text.primary",
                        },
                      }}
                    >
                      {item.day}
                    </Box>
                  );
                })}

              {/* ── RIGHT: next‑month button ── */}
              {extendedDays.length > 0 &&
                extendedDays[extendedDays.length - 1].isPrev === false &&
                !extendedDays[extendedDays.length - 1].isCurrentMonth && (
                  <Box
                    onClick={() => {
                      const nextItem = extendedDays[extendedDays.length - 1];
                      setSelectedMonth(nextItem.label);
                      setSelectedDate(1);
                    }}
                    sx={{
                      minWidth: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 0.75,
                      cursor: "pointer",
                      fontSize: "0.72rem",
                      fontWeight: 400,
                      bgcolor: "grey.50",
                      color: "text.disabled",
                      border: "1px dashed #ccc",
                      px: 1,
                      "&:hover": {
                        bgcolor: "grey.200",
                        color: "text.primary",
                      },
                    }}
                  >
                    {extendedDays[extendedDays.length - 1].label.split(" ")[0]}
                  </Box>
                )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            borderSpacing: "0 8px",
          }}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {[
                ["Vehicle", "46px"],
                ["Driver", "46px"],
                ["Start Date & Time", "92px"],
                ["End Date & Time", "92px"],
                ["Vehicle Type", "58px"],
                ["Customer", "120px"],
                ["Trip Details", "210px"],
                ["Start KM", "54px"],
                ["End KM", "54px"],
                ["KM Run", "54px"],
                ["Driver Bata", "60px"],
                ["Hire", "60px"],
                ["Action", "46px"],
              ].map(([label, width]) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    py: 0.5 /* was 0.75 */,
                    px: 0.75 /* was 1 */,
                    width,
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleBookings.map((row) => (
              <TripRow
                key={row.booking.booking_id}
                row={row}
                edited={editingRows[row.booking.booking_id]}
                isSaving={!!savingRows[row.booking.booking_id]}
                isExpanded={!!expandedRows[row.booking.booking_id]}
                handleFieldChange={handleFieldChange}
                handleSaveRow={onSaveRow}
                toggleTripExpand={toggleTripExpand}
              />
            ))}

            {filteredBookings.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 2.5 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    {searchData
                      ? "No trips match these filters"
                      : "No trips found for this date"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
